import React, { useState, useCallback, useMemo } from 'react';
import {
  Award,
  Users,
  UserPlus,
  UserMinus,
  Calendar,
  Package,
  Search,
  CheckCircle,
  AlertTriangle,
  ShoppingCart,
} from 'lucide-react';

// --- Utility Functions & Mock Data ---

// Mocking a professional logging/notification system (no 'alert()')
const useNotification = () => {
  const [message, setMessage] = useState('');
  const [visible, setVisible] = useState(false);
  const [type, setType] = useState('info'); // info, success, danger

  const notify = (msg, msgType = 'info') => {
    setMessage(msg);
    setType(msgType);
    setVisible(true);
    setTimeout(() => setVisible(false), 3000);
  };
  
  return { notify, message, visible, type };
};

// Main license details
const MOCK_LICENSE_INFO = {
  planName: 'Enterprise Business Plan',
  totalSeats: 100,
  renewalDate: '2026-10-31',
  modules: ['HRMS', 'CRM', 'Payroll', 'L&D', 'Support', 'Tracking'],
};

// Users who currently hold a license
const MOCK_ASSIGNED_USERS = [
  { id: 1, name: 'Jane Doe', email: 'jane.doe@corp.com', department: 'Product Development', assignedDate: '2025-01-15' },
  { id: 2, name: 'Alex Chen', email: 'alex.chen@corp.com', department: 'Sales & Marketing', assignedDate: '2025-02-20' },
  { id: 3, name: 'Sarah Bauer', email: 'sarah.bauer@corp.com', department: 'Finance & Accounting', assignedDate: '2025-03-10' },
  { id: 4, name: 'Mike Vance', email: 'mike.vance@corp.com', department: 'Executive Leadership', assignedDate: '2025-01-01' },
];

// Users who do *not* have a license yet
const MOCK_UNASSIGNED_USERS = [
  { id: 101, name: 'David Kim', email: 'david.kim@corp.com', department: 'Product Development' },
  { id: 102, name: 'Emily Rodriguez', email: 'emily.r@corp.com', department: 'Sales & Marketing' },
  { id: 103, name: 'Peter Jones', email: 'peter.jones@corp.com', department: 'Finance & Accounting' },
];

// --- Reusable UI Components ---

// NOTE: Updated color to Blue
const Panel = ({ title, icon: Icon, children, className = '' }) => (
  <div className={`bg-white p-4 sm:p-5 rounded-xl shadow-lg border border-gray-100 transition-shadow hover:shadow-xl ${className}`}>
    <h2 className="text-sm font-semibold text-gray-700 flex items-center mb-4 pb-2 border-b border-gray-100">
      <Icon className="w-4 h-4 mr-2 text-blue-600" />
      {title}
    </h2>
    <div className="text-xs text-gray-600 space-y-3">
      {children}
    </div>
  </div>
);

// NOTE: Updated color to Blue and kept small padding (px-2 py-1)
const ButtonPrimary = ({ onClick, children, className = '', disabled = false, icon: Icon }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`px-2 py-1 text-xs font-medium rounded-lg shadow-md transition-all duration-200 flex items-center justify-center
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 
                bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 disabled:bg-gray-300 disabled:text-gray-500 ${className}`}
  >
    {Icon && <Icon className="w-3 h-3 mr-1" />}
    {children}
  </button>
);

const Badge = ({ children, type = 'info' }) => {
  let color = 'bg-gray-100 text-gray-600';
  if (type === 'blue') color = 'bg-blue-100 text-blue-700';

  return (
    <span className={`px-2 py-0.5 inline-flex text-[10px] leading-4 font-semibold rounded-full ${color}`}>
      {children}
    </span>
  );
};

// --- Feature Components ---

const LicenseUsageKPI = ({ used, total }) => {
  const percentage = Math.round((used / total) * 100);
  const isNearLimit = percentage > 90;

  return (
    <Panel title="License Utilization" icon={Users} className="lg:col-span-2">
        <div className='flex justify-between items-baseline mb-1'>
            <span className='text-2xl font-bold text-gray-800'>{used} / {total}</span>
            <span className='text-sm font-semibold text-gray-600'>{percentage}% Used</span>
        </div>
      
      {/* Progress Bar */}
      <div className="w-full bg-gray-200 rounded-full h-3">
        <div 
          className={`h-3 rounded-full transition-all duration-500 ${isNearLimit ? 'bg-red-500' : 'bg-blue-600'}`}
          style={{ width: `${percentage}%` }}
        ></div>
      </div>
      
      {isNearLimit && (
        <div className='flex items-center text-red-600 text-[11px] mt-2 p-2 bg-red-50 rounded-lg'>
            <AlertTriangle className='w-3 h-3 mr-2' />
            You are at {percentage}% capacity. Please contact sales to purchase additional seats.
        </div>
      )}
    </Panel>
  );
};

const LicenseDetails = ({ planName, renewalDate, modules, onPurchase }) => (
  <Panel title="Plan Details" icon={Package} className="lg:col-span-1">
    <div className='space-y-2'>
        <div>
            <span className='text-[10px] text-gray-500'>Current Plan</span>
            <p className='text-sm font-semibold text-gray-800'>{planName}</p>
        </div>
        <div>
            <span className='text-[10px] text-gray-500'>Renews On</span>
            <p className='text-sm font-medium text-gray-700 flex items-center'>
                <Calendar className='w-3 h-3 mr-1 text-gray-400' />
                {new Date(renewalDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
        </div>
        <div>
            <span className='text-[10px] text-gray-500'>Enabled Modules</span>
            <div className='flex flex-wrap gap-1 mt-1'>
                {modules.map(mod => <Badge key={mod} type="blue">{mod}</Badge>)}
            </div>
        </div>
    </div>
    
    <ButtonPrimary onClick={onPurchase} className="w-full mt-4" icon={ShoppingCart}>
        Manage Billing or Purchase Seats
    </ButtonPrimary>
  </Panel>
);

const ManageUserAssignments = ({ assignedUsers, unassignedUsers, notify, onAssign, onRevoke }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState('');

  const filteredAssignedUsers = useMemo(() => {
    const searchLower = searchTerm.toLowerCase();
    return assignedUsers.filter(user => 
      user.name.toLowerCase().includes(searchLower) ||
      user.email.toLowerCase().includes(searchLower) ||
      user.department.toLowerCase().includes(searchLower)
    );
  }, [assignedUsers, searchTerm]);

  const handleAssign = () => {
    if (!selectedUser) {
      return notify('Please select a user to assign.', 'danger');
    }
    onAssign(parseInt(selectedUser));
    setSelectedUser(''); // Reset dropdown
  };

  return (
    <Panel title="Manage Assigned Users" icon={Users} className="lg:col-span-3">
      {/* 1. Assign New User Form */}
      <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
        <h4 className="text-[11px] font-semibold text-gray-700 mb-2">Assign License to User</h4>
        <div className="flex flex-col sm:flex-row gap-2">
          <select
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
            className="flex-grow px-2 py-1 border border-gray-300 rounded-lg text-xs appearance-none bg-white"
          >
            <option value="">-- Select an unassigned employee --</option>
            {unassignedUsers.map(user => (
              <option key={user.id} value={user.id}>
                {user.name} ({user.email})
              </option>
            ))}
          </select>
          <ButtonPrimary 
            onClick={handleAssign} 
            icon={UserPlus} 
            className="w-full sm:w-auto"
            disabled={unassignedUsers.length === 0}
          >
            Assign
          </ButtonPrimary>
        </div>
        {unassignedUsers.length === 0 && (
            <p className='text-[10px] text-gray-500 mt-1'>All available users have been assigned a license.</p>
        )}
      </div>

      {/* 2. Filter Assigned Users */}
      <div className='relative mt-4'>
        <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3 h-3 text-gray-400" />
        <input
            type="text"
            placeholder="Search assigned users by name, email, or department..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-7 pr-2 py-1 border border-gray-300 rounded-lg text-xs"
        />
      </div>

      {/* 3. Assigned Users Table */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 mt-2">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {['Name', 'Email', 'Department', 'Date Assigned', 'Action'].map((header) => (
                <th key={header} className="px-3 py-2 text-left text-[11px] font-medium text-gray-500 tracking-wider">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {filteredAssignedUsers.map((user) => (
              <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-3 py-2 whitespace-nowrap text-xs font-medium text-gray-900">{user.name}</td>
                <td className="px-3 py-2 whitespace-nowrap text-xs text-blue-700">{user.email}</td>
                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">{user.department}</td>
                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">{user.assignedDate}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <button
                    onClick={() => onRevoke(user.id)}
                    className="flex items-center px-2 py-1 text-xs font-medium rounded-lg text-red-600 hover:bg-red-100 transition-colors"
                    title={`Revoke license from ${user.name}`}
                  >
                    <UserMinus className="w-3 h-3 mr-1" />
                    Revoke
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {filteredAssignedUsers.length === 0 && (
        <div className="text-center py-4 text-sm text-gray-500">
            {searchTerm ? 'No users found matching your search.' : 'No users are currently assigned.'}
        </div>
      )}
    </Panel>
  );
};


// --- Main Component ---

export default function LicenseManagement() {
  const [licenseInfo, setLicenseInfo] = useState(MOCK_LICENSE_INFO);
  const [assignedUsers, setAssignedUsers] = useState(MOCK_ASSIGNED_USERS);
  const [unassignedUsers, setUnassignedUsers] = useState(MOCK_UNASSIGNED_USERS);
  
  // We mock the 'used' count based on the array length
  const usedSeats = useMemo(() => assignedUsers.length, [assignedUsers]);
  
  const { notify, message, visible, type } = useNotification();
  
  // Custom Styles
  const pageStyle = {
    fontFamily: '"Roboto", "Poppins", sans-serif',
  };
  
  // Notification color logic
  const notificationColor = useMemo(() => {
    if (type === 'success') return 'bg-green-600';
    if (type === 'danger') return 'bg-red-600';
    return 'bg-gray-800';
  }, [type]);

  const handleAssignUser = useCallback((userId) => {
    if (usedSeats >= licenseInfo.totalSeats) {
        return notify('No available licenses. Please purchase additional seats.', 'danger');
    }
    
    // 1. Find user to assign
    const userToAssign = unassignedUsers.find(u => u.id === userId);
    if (!userToAssign) return;

    // 2. Add to assigned list
    const newlyAssignedUser = {
        ...userToAssign,
        assignedDate: new Date().toISOString().split('T')[0],
    };
    setAssignedUsers(prev => [...prev, newlyAssignedUser].sort((a,b) => a.name.localeCompare(b.name)));

    // 3. Remove from unassigned list
    setUnassignedUsers(prev => prev.filter(u => u.id !== userId));
    
    notify(`License assigned to ${userToAssign.name}.`, 'success');
  }, [unassignedUsers, usedSeats, licenseInfo.totalSeats, notify]);

  const handleRevokeUser = useCallback((userId) => {
    // 1. Find user to revoke
    const userToRevoke = assignedUsers.find(u => u.id === userId);
    if (!userToRevoke) return;
    
    // 2. Add back to unassigned list
    const { assignedDate, ...unassignedVersion } = userToRevoke;
    setUnassignedUsers(prev => [...prev, unassignedVersion].sort((a,b) => a.name.localeCompare(b.name)));
    
    // 3. Remove from assigned list
    setAssignedUsers(prev => prev.filter(u => u.id !== userId));
    
    notify(`License revoked from ${userToRevoke.name}.`, 'danger');
  }, [assignedUsers, notify]);
  
  const handlePurchase = () => {
    notify('Redirecting to billing portal... (Mock Action)', 'info');
  };

  return (
    <div className="p-4 sm:p-8 bg-gray-50 min-h-screen" style={pageStyle}>
      {/* HEADER */}
      <div className="mb-8 border-b border-gray-200 pb-4">
        <h1 className="text-xl font-bold text-gray-800 flex items-center">
          <Award className="w-6 h-6 mr-3 text-blue-600" />
          License Management
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Monitor license usage, manage user seat assignments, and view plan details.
        </p>
      </div>

      {/* Main Grid Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <LicenseUsageKPI 
            used={usedSeats}
            total={licenseInfo.totalSeats}
        />
        <LicenseDetails 
            planName={licenseInfo.planName}
            renewalDate={licenseInfo.renewalDate}
            modules={licenseInfo.modules}
            onPurchase={handlePurchase}
        />
        <ManageUserAssignments 
            assignedUsers={assignedUsers}
            unassignedUsers={unassignedUsers}
            notify={notify}
            onAssign={handleAssignUser}
            onRevoke={handleRevokeUser}
        />
      </div>

      {/* Custom Notification Box (Instead of alert()) */}
      <div 
        className={`fixed bottom-4 right-4 p-3 rounded-lg shadow-xl text-xs font-medium transition-all duration-300 text-white 
                    ${visible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'} ${notificationColor}`}
      >
        {message}
      </div>
    </div>
  );
}